import React, { useState, useEffect, useRef } from 'react';
import type { User, PathwayBlock, OjtSession, OjtMessage, OjtAgentRun, OjtMinipageToken, OjtAgentPhase } from '../../../types';
import { DataService, db } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { doc, setDoc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { 
  Send, 
  Mic, 
  Camera, 
  Image as ImageIcon, 
  CheckCircle2, 
  AlertCircle, 
  X, 
  ChevronRight, 
  Sparkles, 
  User as UserIcon,
  Info,
  ListTodo,
  Check
} from 'lucide-react';

interface OjtAgentChatProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
  sessionId: string | null;
  pathwayBlocks?: PathwayBlock[];
  onComplete: () => void;
  onCancel: () => void;
}

export const OjtAgentChat: React.FC<OjtAgentChatProps> = ({
  user,
  block,
  pathwayId,
  sessionId: initialSessionId,
  onComplete,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState<string | null>(initialSessionId);
  const [session, setSession] = useState<OjtSession | null>(null);
  const [agentRun, setAgentRun] = useState<OjtAgentRun | null>(null);
  const [messages, setMessages] = useState<OjtMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  
  // Selection of supervisor for new sessions
  const [leadHands, setLeadHands] = useState<any[]>([]);
  const [selectedLeadHand, setSelectedLeadHand] = useState<any>(null);
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  // Objectives drawer state
  const [showObjectives, setShowObjectives] = useState(false);

  // Minipage modal states
  const [activeTokenId, setActiveTokenId] = useState<string | null>(null);
  const [activeToken, setActiveToken] = useState<OjtMinipageToken | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [formText, setFormText] = useState('');
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [capturedSelfieUrl, setCapturedSelfieUrl] = useState<string | null>(null);

  // Refs
  const messageEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawingRef = useRef(false);

  // Speech Recognition API setup
  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (SpeechRecognition) {
      const rec = new SpeechRecognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.onresult = (e: any) => {
        const text = Array.from(e.results).map((r: any) => r[0].transcript).join('');
        setInputText(text);
      };
      rec.onend = () => setIsRecording(false);
      recognitionRef.current = rec;
    }
  }, []);

  // Fetch lead hands if we are starting a new session
  useEffect(() => {
    if (!sessionId && block.leadHandIds && block.leadHandIds.length > 0) {
      DataService.getLeadHandDetails(block.leadHandIds)
        .then(setLeadHands)
        .catch(console.error);
    }
  }, [sessionId, block.leadHandIds]);

  // Subscribe to OJT messages and Agent Run state once session exists
  useEffect(() => {
    if (!sessionId) return;

    // Real-time messages listener
    const unsubscribeMessages = DataService.subscribeOjtMessages(sessionId, (msgs) => {
      // Sort ascending just to be safe
      const sorted = [...msgs].sort((a, b) => a.createdAt - b.createdAt);
      setMessages(sorted);
      setTimeout(() => messageEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });

    // Real-time OJT session listener
    const unsubscribeSession = DataService.onOjtSessionChange(sessionId, (sess) => {
      setSession(sess);
      if (sess.status === 'COMPLETED' || sess.status === 'REVIEW_PENDING') {
        // Session complete/review pending - we can notify user or redirect
      }
    });

    // Real-time Agent Run state listener
    const runDocRef = doc(db, 'soloready', 'data', 'ojt_agent_runs', sessionId);
    const unsubscribeRun = onSnapshot(runDocRef, (snap) => {
      if (snap.exists()) {
        setAgentRun(snap.data() as OjtAgentRun);
      }
    });

    return () => {
      unsubscribeMessages();
      unsubscribeSession();
      unsubscribeRun();
    };
  }, [sessionId]);

  // Handle Speech-to-Text toggle
  const toggleRecording = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      recognitionRef.current.start();
      setIsRecording(true);
    }
  };

  // Handle creation of a new session
  const handleCreateSession = async () => {
    if (!selectedLeadHand) return;
    setIsCreatingSession(true);
    try {
      const now = Date.now();
      const newSession: Omit<OjtSession, 'id'> = {
        title: `${block.name} - ${new Date().toLocaleDateString()}`,
        candidateId: user.id,
        candidateName: user.name,
        leadHandId: selectedLeadHand.id,
        leadHandName: selectedLeadHand.name,
        pathwayId,
        blockId: block.id,
        orgId: user.activeOrgId || '',
        date: now,
        startTime: now,
        status: 'SCHEDULED',
        elements: [],
        requiredTimeMinutes: (block.ojtConfig?.expectedDurationHours || 0) * 60,
        loggedTimeMinutes: 0,
        handshakeVerified: false,
        preStartChecklistCompleted: false,
        objectives: block.ojtConfig?.objectives?.map(obj => ({
          objectiveId: obj.id,
          label: obj.label,
          isCompleted: false,
          evidence: { photos: [], videos: [], voiceRecordings: [] },
        })) || [],
      };

      // Add custom compatibility fields for the agent triggers
      (newSession as any).candidateUid = user.id;
      (newSession as any).leadHandUid = selectedLeadHand.id;
      (newSession as any).scheduledStartAt = new Date();

      const created = await DataService.createOjtSession(newSession);
      
      // Ensure the ojt_block document exists in Firestore so the agent runner can load it
      await setDoc(doc(db, 'soloready', 'data', 'ojt_blocks', block.id), {
        id: block.id,
        title: block.name,
        agentEnabled: true,
        objectives: block.ojtConfig?.objectives || [],
        preStartChecklist: block.ojtConfig?.preStartChecklist || [],
        expectedDurationHours: block.ojtConfig?.expectedDurationHours || 0,
      });

      setSessionId(created.id);

      // Write initial message to wake up the agent runner trigger
      await DataService.sendOjtChatMessage(created.id, "Hello! I am ready to start my OJT session.");
    } catch (e) {
      console.error('Failed to create session:', e);
    } finally {
      setIsCreatingSession(false);
    }
  };

  // Send message from chat box
  const handleSendMessage = async () => {
    if (!inputText.trim() || !sessionId) return;
    const text = inputText.trim();
    setInputText('');
    try {
      await DataService.sendOjtChatMessage(sessionId, text);
    } catch (e) {
      console.error('Failed to send message:', e);
    }
  };

  // Handle file uploads (photo/evidence)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;
    setIsUploading(true);
    try {
      const storagePath = `ojt/${sessionId}/chat/${Date.now()}_${file.name}`;
      const downloadUrl = await DataService.uploadBlockFile(file, storagePath);
      
      // Send a message containing the uploaded file URL
      await DataService.sendOjtChatMessage(
        sessionId,
        `Sent photo attachment: ${file.name}`
      );
      
      // Add message with payload so we render it as an image bubble
      await DataService.sendOjtChatMessage(sessionId, downloadUrl);
    } catch (e) {
      console.error('Failed to upload file:', e);
    } finally {
      setIsUploading(false);
    }
  };

  // Check if a text body is a minipage URL and extract its token
  const parseMinipageToken = (body: string): string | null => {
    const match = body.match(/\/m\/([a-zA-Z0-9_-]+)/);
    return match ? match[1] : null;
  };

  // Fetch token details when user clicks a minipage card
  const handleOpenMinipage = async (token: string) => {
    setActiveTokenId(token);
    setIsLoadingToken(true);
    setTokenError('');
    setFormText('');
    setCapturedSelfieUrl(null);
    setFormSubmitted(false);

    try {
      const ref = doc(db, 'soloready', 'data', 'ojt_minipage_tokens', token);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        setTokenError('This form is no longer valid.');
        return;
      }
      const data = snap.data() as OjtMinipageToken;
      if (data.consumedAt != null) {
        setTokenError('This form has already been submitted.');
        return;
      }
      if (data.expiresAt && data.expiresAt <= Date.now()) {
        setTokenError('This form link has expired.');
        return;
      }
      setActiveToken({ ...data, token });
    } catch (e: any) {
      console.error('Error fetching token:', e);
      if (e.code === 'permission-denied') {
        setTokenError('This form is no longer valid or has expired.');
      } else {
        setTokenError('Something went wrong loading this form.');
      }
    } finally {
      setIsLoadingToken(false);
    }
  };

  // Submit the minipage form
  const handleFormSubmit = async () => {
    if (!activeToken || !sessionId) return;
    setIsSubmittingForm(true);
    try {
      const functions = getFunctions(getApp(), 'europe-west2');
      const submitMinipage = httpsCallable(functions, 'submitMinipage');

      let responsePayload: any = {};
      if (activeToken.kind === 'free_text' || activeToken.kind === 'wrap_up') {
        responsePayload = { text: formText.trim() };
      } else if (activeToken.kind === 'selfie') {
        responsePayload = { storagePath: capturedSelfieUrl, stub: false };
      } else if (activeToken.kind === 'sign_off') {
        // For signature
        const canvas = canvasRef.current;
        const signatureData = canvas ? canvas.toDataURL('image/png') : '';
        const signaturePath = `ojt/${sessionId}/signatures/${Date.now()}_leadhand.png`;
        
        // Upload base64 signature to firebase storage
        const blob = await (await fetch(signatureData)).blob();
        const file = new File([blob], 'signature.png', { type: 'image/png' });
        const signatureUrl = await DataService.uploadBlockFile(file, signaturePath);

        responsePayload = { signatureUrl, signaturePath, stub: false };
      } else {
        responsePayload = { stub: true, kind: activeToken.kind };
      }

      await submitMinipage({ token: activeToken.token, response: responsePayload });
      
      setFormSubmitted(true);
      setTimeout(() => {
        setActiveTokenId(null);
        setActiveToken(null);
      }, 1500);
    } catch (e: any) {
      console.error('submitMinipage failed', e);
      setTokenError(e?.message || 'Could not submit form. Please try again.');
    } finally {
      setIsSubmittingForm(false);
    }
  };

  // Camera capture helper for Selfie minipage
  const handleSelfieCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !sessionId) return;
    setIsUploading(true);
    try {
      const path = `ojt/${sessionId}/selfie/${Date.now()}_selfie.jpg`;
      const url = await DataService.uploadBlockFile(file, path);
      setCapturedSelfieUrl(url);
    } catch (e) {
      console.error('Selfie upload failed:', e);
      alert('Failed to upload selfie. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Canvas drawing listeners for signature
  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    isDrawingRef.current = true;
    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e) ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = ('touches' in e) ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    const x = ('touches' in e) ? e.touches[0].clientX - rect.left : e.clientX - rect.left;
    const y = ('touches' in e) ? e.touches[0].clientY - rect.top : e.clientY - rect.top;

    ctx.lineTo(x, y);
    ctx.strokeStyle = '#0284c7';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Helper to map current phase to human text
  const getPhaseName = (phase?: OjtAgentPhase): string => {
    switch (phase) {
      case 'READINESS_CHECK': return 'Readiness Check';
      case 'SELFIE_VERIFICATION': return 'Selfie Check';
      case 'OBJECTIVE_SETTING': return 'Setting Objectives';
      case 'PRE_START_CHECKLIST': return 'Safety PPE Check';
      case 'SESSION_BODY': return 'Training in Progress';
      case 'WRAP_UP': return 'Feedback & Wrap Up';
      case 'SIGN_OFF': return 'Supervisor Sign-off';
      case 'COMPLETE': return 'Completed';
      case 'NO_SHOW': return 'No Show';
      case 'FAILED': return 'Failed';
      default: return 'Starting Session';
    }
  };

  // Helper to get Phase progress percentage
  const getPhaseProgress = (phase?: OjtAgentPhase): number => {
    const steps: OjtAgentPhase[] = [
      'READINESS_CHECK',
      'SELFIE_VERIFICATION',
      'OBJECTIVE_SETTING',
      'PRE_START_CHECKLIST',
      'SESSION_BODY',
      'WRAP_UP',
      'SIGN_OFF',
      'COMPLETE'
    ];
    if (!phase) return 0;
    const idx = steps.indexOf(phase);
    if (idx === -1) return 0;
    return Math.round(((idx + 1) / steps.length) * 100);
  };

  // --- RENDERING ---

  if (!sessionId) {
    // Show select supervisor screen
    return (
      <div className="p-4 max-w-md mx-auto space-y-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-aero-200 p-5 shadow-sm">
          <h2 className="text-lg font-bold text-aero-900 dark:text-slate-50 mb-1">Start Coach-Led Training</h2>
          <p className="text-sm text-aero-500 dark:text-slate-400">
            You are starting an agentic OJT session. Please select the supervisor who is present or will verify your performance.
          </p>
        </div>

        <div className="space-y-2">
          {leadHands.length === 0 && (
            <div className="bg-aero-50 dark:bg-slate-800 rounded-2xl border border-aero-100 p-4 text-center">
              <p className="text-sm text-aero-500">No supervisors assigned to this block.</p>
            </div>
          )}
          {leadHands.map((lh) => (
            <button
              key={lh.id}
              onClick={() => setSelectedLeadHand(lh)}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 transition-all text-left ${
                selectedLeadHand?.id === lh.id
                  ? 'border-sky-500 bg-sky-50 dark:bg-sky-950'
                  : 'border-aero-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-aero-400'
              }`}
            >
              <div className="w-10 h-10 rounded-full bg-aero-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-bold text-aero-600 dark:text-slate-200">{lh.name?.charAt(0)?.toUpperCase()}</span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-aero-900 dark:text-slate-100">{lh.name}</p>
                {lh.email && <p className="text-xs text-aero-400">{lh.email}</p>}
              </div>
              {selectedLeadHand?.id === lh.id && (
                <div className="w-5 h-5 rounded-full bg-sky-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-3 pt-2">
          <button 
            onClick={onCancel}
            className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 rounded-xl font-semibold text-sm transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleCreateSession}
            disabled={!selectedLeadHand || isCreatingSession}
            className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-gradient-to-r from-sky-500 to-aero-500 hover:from-sky-600 hover:to-aero-600 disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-400 text-white rounded-xl font-semibold text-sm transition-all shadow-sm"
          >
            {isCreatingSession ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white border-t-transparent id-loader rounded-full animate-spin"></span>
                Creating...
              </span>
            ) : (
              <>
                Let's Go
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-140px)] max-w-2xl mx-auto relative bg-slate-50 dark:bg-slate-950 rounded-2xl border border-aero-200 shadow-sm overflow-hidden">
      {/* HEADER SECTION */}
      <header className="px-4 py-3 bg-white dark:bg-slate-900 border-b border-aero-100 flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 bg-sky-100 dark:bg-sky-950 rounded-full flex items-center justify-center relative">
              <Sparkles className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white dark:border-slate-900 rounded-full animate-pulse" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 leading-tight">AI Coach &bull; {block.name}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="text-[10px] font-semibold text-sky-600 dark:text-sky-400 uppercase tracking-wider">{getPhaseName(agentRun?.phase)}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {session?.objectives && (
              <button 
                onClick={() => setShowObjectives(prev => !prev)}
                className={`p-2 rounded-xl transition-colors flex items-center gap-1.5 text-xs font-semibold ${
                  showObjectives ? 'bg-sky-100 text-sky-600 dark:bg-sky-950 dark:text-sky-400' : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
                }`}
              >
                <ListTodo className="w-4 h-4" />
                Goals
              </button>
            )}
            <button 
              onClick={onComplete}
              className="p-2 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-xl transition-colors text-xs font-semibold"
            >
              Exit
            </button>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 rounded-full overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-sky-500 to-aero-500 transition-all duration-500" 
            style={{ width: `${getPhaseProgress(agentRun?.phase)}%` }} 
          />
        </div>
      </header>

      {/* FLOAT CHECKLIST DRAWER */}
      {showObjectives && session?.objectives && (
        <div className="absolute top-[68px] right-4 w-72 bg-white dark:bg-slate-900 border border-aero-150 rounded-2xl p-4 shadow-xl z-20 transition-all animate-in fade-in slide-in-from-top-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5 text-sky-500" />
              OJT Objectives
            </h4>
            <button onClick={() => setShowObjectives(false)}>
              <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            </button>
          </div>
          <div className="space-y-2.5 max-h-64 overflow-y-auto">
            {session.objectives.map((obj, i) => (
              <div key={obj.objectiveId} className="flex gap-2 items-start text-xs border-b border-slate-50 dark:border-slate-850 pb-2">
                <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center border ${
                  obj.isCompleted ? 'bg-green-500 border-green-500 text-white' : 'border-slate-300 dark:border-slate-700 text-transparent'
                }`}>
                  <Check className="w-2.5 h-2.5" />
                </span>
                <div>
                  <span className="font-semibold text-slate-800 dark:text-slate-200">Goal {i+1}: </span>
                  <span className="text-slate-600 dark:text-slate-400">{obj.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MESSAGES TIMELINE */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((msg) => {
          // If the message body is a download URL from storage (image)
          const isImage = msg.body.startsWith('https://firebasestorage.googleapis.com/') && (msg.body.includes('/chat/') || msg.body.includes('/selfie/'));
          const minipageToken = parseMinipageToken(msg.body);
          
          if (msg.role === 'system') {
            return (
              <div key={msg.id} className="flex justify-center my-2">
                <div className="bg-aero-50 dark:bg-slate-900 border border-aero-100 px-3 py-1.5 rounded-full text-[11px] text-aero-600 dark:text-slate-400 flex items-center gap-1.5 font-medium shadow-sm">
                  <Info className="w-3.5 h-3.5" />
                  {msg.body}
                </div>
              </div>
            );
          }

          const isCandidate = msg.role === 'candidate';
          
          return (
            <div key={msg.id} className={`flex ${isCandidate ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm text-sm ${
                isCandidate
                  ? 'bg-gradient-to-r from-sky-600 to-aero-600 text-white rounded-br-none'
                  : 'bg-white dark:bg-slate-900 border border-aero-100 text-slate-800 dark:text-slate-100 rounded-bl-none'
              }`}>
                {/* Image message */}
                {isImage ? (
                  <div className="rounded-lg overflow-hidden border border-slate-150 dark:border-slate-800">
                    <img src={msg.body} alt="Attached evidence" className="max-h-48 object-cover cursor-zoom-in" onClick={() => window.open(msg.body, '_blank')} />
                  </div>
                ) : (
                  <p className="whitespace-pre-wrap">{msg.body}</p>
                )}

                {/* Minipage Intercept Card */}
                {minipageToken && (
                  <div className="mt-3 pt-3 border-t border-dashed border-aero-200 dark:border-slate-800">
                    <div className="bg-slate-50 dark:bg-slate-950 border border-aero-100 rounded-xl p-3.5 flex flex-col gap-2.5">
                      <div className="flex items-start gap-2.5">
                        <div className="w-8 h-8 rounded-lg bg-sky-100 dark:bg-sky-950 flex items-center justify-center flex-shrink-0">
                          <Camera className="w-4 h-4 text-sky-600 dark:text-sky-400" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Required Action Form</p>
                          <p className="text-[11px] text-slate-400 mt-0.5">Please fill out this quick form inside the chat cockpit.</p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => handleOpenMinipage(minipageToken)}
                        className="w-full py-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold text-xs rounded-lg transition-colors flex items-center justify-center gap-1.5"
                      >
                        Launch Form
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Timestamp */}
                <div className={`text-[9px] mt-1 text-right ${isCandidate ? 'text-sky-200' : 'text-slate-400'}`}>
                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={messageEndRef} />
      </div>

      {/* CHAT INPUT BAR */}
      <div className="p-3 bg-white dark:bg-slate-900 border-t border-aero-100 flex items-center gap-2.5">
        {/* Attachment menu trigger */}
        <label className="p-2 bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400 rounded-xl cursor-pointer transition-colors relative flex items-center justify-center">
          <Camera className="w-4.5 h-4.5" />
          <input 
            type="file" 
            accept="image/*" 
            className="hidden" 
            onChange={handleFileUpload}
            disabled={isUploading}
          />
          {isUploading && (
            <span className="absolute inset-0 bg-white/70 dark:bg-slate-800/70 flex items-center justify-center rounded-xl">
              <span className="w-4 h-4 border-2 border-sky-500 border-t-transparent rounded-full animate-spin"></span>
            </span>
          )}
        </label>
        
        {/* Text Input Box */}
        <input
          type="text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSendMessage()}
          placeholder="Send a message to your Coach..."
          className="flex-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all"
        />

        {/* Speech to Text Mic Button */}
        <button
          onClick={toggleRecording}
          className={`p-2.5 rounded-xl transition-colors relative ${
            isRecording 
              ? 'bg-red-500 text-white animate-pulse' 
              : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-slate-400'
          }`}
        >
          <Mic className="w-4.5 h-4.5" />
        </button>

        {/* Send text button */}
        <button
          onClick={handleSendMessage}
          disabled={!inputText.trim()}
          className="p-2.5 bg-sky-500 hover:bg-sky-600 disabled:bg-slate-100 dark:disabled:bg-slate-800 text-white dark:disabled:text-slate-600 rounded-xl transition-colors"
        >
          <Send className="w-4.5 h-4.5" />
        </button>
      </div>

      {/* MINIPAGE POPUP MODAL */}
      {activeTokenId && (
        <div className="absolute inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-30 animate-in fade-in">
          <div className="w-full max-w-md bg-white dark:bg-slate-900 border border-aero-150 rounded-2xl shadow-xl overflow-hidden animate-in zoom-in-95">
            
            {/* Modal Header */}
            <div className="px-5 py-3.5 border-b border-aero-100 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-900">
              <div>
                <span className="text-[9px] font-bold text-sky-600 dark:text-sky-400 uppercase tracking-widest">
                  {activeToken?.forActor === 'lead_hand' ? 'Supervisor Sign-off' : 'Trainee Action'}
                </span>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                  {activeToken ? activeToken.prompt : 'Loading form...'}
                </h3>
              </div>
              <button 
                onClick={() => {
                  setActiveTokenId(null);
                  setActiveToken(null);
                }} 
                className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 max-h-[70vh] overflow-y-auto">
              {isLoadingToken && (
                <div className="py-8 flex flex-col items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                  <p className="text-xs text-slate-400">Loading form properties...</p>
                </div>
              )}

              {tokenError && (
                <div className="py-6 flex flex-col items-center gap-3 text-center">
                  <AlertCircle className="w-10 h-10 text-red-500" />
                  <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{tokenError}</p>
                  <button 
                    onClick={() => {
                      setActiveTokenId(null);
                      setActiveToken(null);
                    }}
                    className="mt-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-xs font-semibold rounded-lg"
                  >
                    Close
                  </button>
                </div>
              )}

              {formSubmitted && (
                <div className="py-8 flex flex-col items-center gap-2 text-center text-green-500">
                  <CheckCircle2 className="w-12 h-12" />
                  <p className="text-sm font-bold">Acknowledge recorded!</p>
                  <p className="text-xs text-slate-400">Updating agent flow...</p>
                </div>
              )}

              {!isLoadingToken && !tokenError && !formSubmitted && activeToken && (
                <div className="space-y-4">
                  {/* FREE TEXT FORM */}
                  {(activeToken.kind === 'free_text' || activeToken.kind === 'wrap_up') && (
                    <div className="space-y-3">
                      <textarea
                        value={formText}
                        onChange={(e) => setFormText(e.target.value)}
                        rows={5}
                        placeholder="Type your response here..."
                        className="w-full text-sm border border-slate-250 dark:border-slate-700 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-sky-500 resize-none"
                      />
                      
                      {/* Speech to Text in Form */}
                      <button
                        type="button"
                        onClick={toggleRecording}
                        className={`w-full py-2.5 rounded-lg text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
                          isRecording ? 'bg-red-500 text-white' : 'bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200'
                        }`}
                      >
                        <Mic className="w-4 h-4" />
                        {isRecording ? 'Stop Recording' : 'Use Voice Input'}
                      </button>
                    </div>
                  )}

                  {/* SELFIE FORM */}
                  {activeToken.kind === 'selfie' && (
                    <div className="space-y-4 flex flex-col items-center">
                      <div className="bg-slate-50 dark:bg-slate-850 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 w-full p-6 text-center">
                        {capturedSelfieUrl ? (
                          <div className="flex flex-col items-center gap-3">
                            <img src={capturedSelfieUrl} alt="Selfie captured" className="h-44 object-cover rounded-xl border border-slate-200" />
                            <p className="text-[11px] text-green-500 font-semibold">Selfie uploaded successfully!</p>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center gap-3">
                            <Camera className="w-10 h-10 text-slate-400" />
                            <div>
                              <p className="text-xs font-bold text-slate-700 dark:text-slate-350">Co-presence Selfie Required</p>
                              <p className="text-[11px] text-slate-400 mt-1">Take a photo showing both you and your supervisor.</p>
                            </div>
                          </div>
                        )}
                      </div>

                      <label className="w-full py-3 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs cursor-pointer flex items-center justify-center gap-2">
                        <Camera className="w-4.5 h-4.5" />
                        {capturedSelfieUrl ? 'Retake Selfie' : 'Capture Selfie'}
                        <input
                          type="file"
                          accept="image/*"
                          capture="user"
                          className="hidden"
                          onChange={handleSelfieCapture}
                          disabled={isUploading}
                        />
                      </label>
                    </div>
                  )}

                  {/* SIGN OFF FORM */}
                  {activeToken.kind === 'sign_off' && (
                    <div className="space-y-3">
                      <div className="bg-sky-50/50 dark:bg-sky-950/20 border border-sky-100 rounded-xl p-3.5 text-xs text-sky-800 dark:text-sky-300">
                        <Info className="w-4 h-4 text-sky-500 float-left mr-2 mt-0.5" />
                        <strong>Attention Lead Hand:</strong> By signing below, you verify that the candidate completed all objectives and demonstrated competency in this block.
                      </div>

                      <div className="relative border border-slate-250 dark:border-slate-800 rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-950">
                        <canvas
                          ref={canvasRef}
                          width={380}
                          height={160}
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                          className="w-full block touch-none cursor-crosshair"
                        />
                        <button
                          type="button"
                          onClick={clearCanvas}
                          className="absolute bottom-2 right-2 px-3 py-1 bg-white hover:bg-slate-100 border border-slate-200 rounded-lg text-[10px] font-semibold text-slate-500 shadow-sm"
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {/* COMING SOON / STUBS */}
                  {activeToken.kind !== 'free_text' && activeToken.kind !== 'wrap_up' && activeToken.kind !== 'selfie' && activeToken.kind !== 'sign_off' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-xs text-amber-800">
                      <strong>Objectives/Verification —</strong> The form layout for <code className="font-mono">{activeToken.kind}</code> is pending. Tap "Confirm" to acknowledge and proceed.
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    onClick={handleFormSubmit}
                    disabled={
                      isSubmittingForm || 
                      isUploading ||
                      ((activeToken.kind === 'free_text' || activeToken.kind === 'wrap_up') && !formText.trim()) ||
                      (activeToken.kind === 'selfie' && !capturedSelfieUrl)
                    }
                    className="w-full py-3.5 bg-gradient-to-r from-sky-500 to-aero-500 hover:from-sky-600 hover:to-aero-600 disabled:from-slate-200 disabled:to-slate-300 disabled:text-slate-400 text-white rounded-xl font-bold text-sm transition-all"
                  >
                    {isSubmittingForm ? 'Submitting...' : activeToken.kind === 'sign_off' ? 'Confirm and Sign Off' : 'Submit Response'}
                  </button>
                </div>
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};
